import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ITooltipState } from './ITooltipState';

const DELAY = 1000;

/**
 * Шар підказок (UIUX/controls/tooltip; прототип _tipOver): замість системних підказок title — темна бульбашка через 1 с наведення,
 * під елементом або над ним біля низу вікна (max-width 280, 12px 500, клас .tooltip). Атрибут title переноситься в data-tip, щоб
 * браузер не показував свою; клік або вихід курсора ховає підказку. Елемент з `data-tip-overflow` (текст клітинок таблиці) показує підказку
 * лише тоді, коли текст обрізано (UIUX field-types/text: «tooltip with the full value when truncated»). Монтується один раз в оболонці.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<ITooltipState | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current: Element | null = null;
    const hide = () => { clearTimeout(timer); current = null; setTip(null); };
    const over = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target.closest('[title],[data-tip]') : null;
      if (!el || el === current) return;
      const title = el.getAttribute('title');
      if (title) { el.setAttribute('data-tip', title); el.removeAttribute('title'); }
      clearTimeout(timer); current = el; setTip(null);
      timer = setTimeout(() => {
        const text = el.getAttribute('data-tip');
        if (current !== el || !text || !document.body.contains(el)) return;
        if (el.hasAttribute('data-tip-overflow') && el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1) return;
        const r = el.getBoundingClientRect();
        const below = r.bottom + 40 < window.innerHeight;
        setTip({ text, x: Math.max(8, Math.min(r.left + r.width / 2, window.innerWidth - 8)), y: below ? r.bottom + 8 : r.top - 8, below });
      }, DELAY);
    };
    const out = (e: MouseEvent) => {
      if (!current || (e.relatedTarget instanceof Node && current.contains(e.relatedTarget))) return;
      hide();
    };
    document.addEventListener('mouseover', over, true);
    document.addEventListener('mouseout', out, true);
    document.addEventListener('mousedown', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseover', over, true);
      document.removeEventListener('mouseout', out, true);
      document.removeEventListener('mousedown', hide, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);
  if (!tip) return null;
  return createPortal(
    <div className="tooltip" role="tooltip" style={{ left: tip.x, top: tip.y, transform: tip.below ? 'translateX(-50%)' : 'translate(-50%, -100%)' }}>{tip.text}</div>,
    document.body,
  );
}
